import { MatomoProvider } from '@jonkoops/matomo-tracker-react';
import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type PropsWithChildren, useMemo } from 'react';

import { AuthProvider, ErrorHandled } from '@furality/frontend-stack';
import { ToastContainer, ToastContextProvider } from '@furality/furality-purpose';
import { useDiscordRedirect } from '#/hooks/useDiscord.ts';
import AuctionNotificationProvider from '@/components/__app/AuctionNotificationProvider.tsx';
import CentrifugeProvider from '@/components/__app/CentrifugeProvider';
import PortalGlobalNotificationsListener from '@/components/__app/centrifuge-listeners/PortalGlobalNotificationsListener';
import PortalUserNotificationsListener from '@/components/__app/centrifuge-listeners/PortalUserNotificationsListener';
import FeaturesProvider from '@/components/__app/FeaturesProvider.tsx';
import FriendLocationProvider from '@/components/__app/FriendLocationProvider';
import I18nLoader from '@/components/__app/I18nLoader.tsx';
import { InstancePreferenceContextProvider } from '@/components/__app/InstancePreferenceContext.tsx';
import Maintenance from '@/components/__app/Maintenance.tsx';
import MatomoTracker, { matomoInstance } from '@/components/__app/MatomoTracker.tsx';
import PageLoadingProvider from '@/components/__app/PageLoadingProvider.tsx';
import Pending from '@/components/__app/Pending.tsx';
import SupportModalProvider from '@/components/__app/SupportModalProvider.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';
import { usePatreonRedirect } from '@/lib/hooks/usePatreon.ts';

// declaration merging
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface MyRouterContext {
  queryClient: QueryClient;
}

const clientId = import.meta.env['PUBLIC_OIDC_CLIENT_ID'];
if (clientId == null) {
  throw new Error('OIDC configuration is missing: PUBLIC_OIDC_CLIENT_ID');
}
const clientScopes = import.meta.env['PUBLIC_OIDC_SCOPES'];
if (clientScopes == null) {
  throw new Error('OIDC configuration is missing: PUBLIC_OIDC_SCOPES');
}
const clientAuthority = import.meta.env['PUBLIC_OIDC_AUTHORITY'];
if (clientAuthority == null) {
  throw new Error('OIDC configuration is missing: PUBLIC_OIDC_AUTHORITY');
}
const redirectUrl = import.meta.env['PUBLIC_OIDC_REDIRECT_URL'];

const ALLOWLIST = ['/login', '/oauth-redirect', '/safety', '/accounts', '/donate', '/checkout'];
const allowed = (path: string) => ALLOWLIST.some((p) => path.includes(p));

const PendingLockout = ({ children }: PropsWithChildren) => {
  const ready = useFlagsStatus().flagsReady;
  const globalMaintenance = useFlag('global.maintenance');
  const pending = useFlag('pending');
  const location = useLocation();
  const search = Route.useSearch();

  const bypass = allowed(location.pathname);

  if (!ready) {
    return <PageLoading />;
  }

  if (!bypass && (globalMaintenance || search.serverMaintenance)) {
    return <Maintenance />;
  }
  if (!bypass && pending) {
    return <Pending />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const RootComponent = () => {
  const navigate = useNavigate();
  usePatreonRedirect();
  useDiscordRedirect();

  const errorCallbacks = useMemo(
    () => ({
      503: () => {
        void navigate({ to: '/', search: { serverMaintenance: true } });
        return ErrorHandled.Handled;
      },
    }),
    [navigate],
  );

  return (
    <I18nLoader>
      <MatomoProvider value={matomoInstance}>
        <AuthProvider
          errorCallbacks={errorCallbacks}
          configuration={{
            clientId,
            authority: clientAuthority,
            scopes: clientScopes,
            redirectionUrl: redirectUrl,
          }}
          routing={{
            basePath: '',
            redirect: (path) => void navigate({ to: path }),
          }}
        >
          <FeaturesProvider>
            <SupportModalProvider>
              <ToastContextProvider>
                <ToastContainer />
                <MatomoTracker>
                  <CentrifugeProvider>
                    <PortalGlobalNotificationsListener />
                    <PortalUserNotificationsListener />
                    <AuctionNotificationProvider />
                    <FriendLocationProvider>
                      <PageLoadingProvider>
                        <PendingLockout>
                          <InstancePreferenceContextProvider>
                            <Outlet />
                          </InstancePreferenceContextProvider>
                        </PendingLockout>
                      </PageLoadingProvider>
                    </FriendLocationProvider>
                  </CentrifugeProvider>
                </MatomoTracker>
              </ToastContextProvider>
            </SupportModalProvider>
          </FeaturesProvider>
        </AuthProvider>
      </MatomoProvider>
    </I18nLoader>
  );
};

const RootSearch = Type.Object({
  serverMaintenance: Type.Optional(Type.Boolean()),

  // discord linking
  error: Type.Optional(Type.String()),

  // checkout flow
  paymentId: Type.Optional(Type.String()),
  orderId: Type.Optional(Type.String()),
});
// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  validateSearch: (search) => Value.Cast(RootSearch, search),
});
